import React from 'react';
import { CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import EqualisationPage from '../EqualisationPage';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import AvatarWithName from 'components/new/AvatarWithName';

interface CapitalContributionsContentProps {
  selectedCommitments: Array<{
    commitmentId: string;
    investorName: string;
    isCarryPartnerCommitment: boolean;
    totalCommitmentLcy: number;
    loanCommitmentLcy: number;
    capitalContributionChange: number;
    capitalCommitmentLcy: number;
    existingCapitalCommitmentLcy?: number;
    isCapitalContributionRuleOverride?: boolean;
    equalisationDate?: CalendarDay | null;
  }>;
  baseCurrency: CurrencyCode;
}

const CapitalContributionsContent: React.FC<
  CapitalContributionsContentProps
> = ({ selectedCommitments, baseCurrency }) => {
  const { previousSection, nextSection, goToNextSection, goToPreviousSection } =
    useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const hasIds = Boolean(id && selectedVehicleId);

  const equalisationQueryKey = getQueryKey(api.equalisations.get, {
    equalisationId: id ?? '',
    vehicleId: selectedVehicleId ?? '',
  });

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationQueryKey],
    }),
  );

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  const rows = selectedCommitments.map((commitment) => {
    const originalCapital = commitment.existingCapitalCommitmentLcy ?? 0;
    const newCapital = commitment.capitalCommitmentLcy;

    return {
      id: commitment.commitmentId,
      investorName: commitment.investorName,
      isCarryPartnerCommitment: commitment.isCarryPartnerCommitment,
      originalCapital,
      newCapital,
      baseCurrency,
      change: commitment.capitalContributionChange,
      equalisationDate: commitment.equalisationDate,
    };
  });

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'investorName',
      label: 'Investor name',
      render: ({ investorName }) => <AvatarWithName name={investorName} />,
    },
    {
      key: 'originalCapital',
      label: 'Original capital contribution',
      render: (row) =>
        row.equalisationDate
          ? numberToCurrencyString(row.originalCapital, {
              fallback: '-',
              currency: row.baseCurrency,
            })
          : '-',
    },
    {
      key: 'newCapital',
      label: 'New capital contribution',
      render: (row) =>
        numberToCurrencyString(row.newCapital, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
    },
    {
      key: 'change',
      label: 'Change',
      render: (row) =>
        numberToCurrencyString(row.change, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  return (
    <EqualisationPage
      actionBar={
        <EqualisationFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          disabled={!hasIds}
          loading={updateEqualisation.isPending}
        />
      }
      subheading="Review capital contributions."
    >
      <Table columns={columns} rows={rows} />
    </EqualisationPage>
  );
};

export default CapitalContributionsContent;
